import type { ReactNode } from 'react';

/**
 * The title of one step in the email → OTP flow, at the level the surrounding
 * document needs.
 *
 * `EmailStep`/`OtpStep` are rendered in two places with different heading
 * contexts: `/register`, where the step IS the page and its title is the
 * document's `<h1>`; and `/settings`, where the step sits inside a card on a
 * page whose `<h1>` is already "Settings". A second `<h1>` there is an
 * accessibility defect, and a 24px display face next to the sibling cards'
 * 16px `<h2>` is a visual one. One component, so the tag and the type scale
 * can never disagree — the mistake a caller-supplied `style` would invite.
 */
export function StepHeading({
  level = 1,
  id,
  children,
}: {
  level?: 1 | 2;
  /**
   * Optional DOM id, so a surrounding `role="dialog"` can name itself from the
   * VISIBLE heading via `aria-labelledby` instead of repeating the text in an
   * `aria-label`. A repeated string is a string that can drift from what is on
   * screen; pointing at the heading cannot. Used by the embedded registration
   * prompt; absent on the page-level callers, which are not dialogs.
   */
  id?: string;
  children: ReactNode;
}) {
  if (level === 2) {
    return (
      <h2 id={id} style={{ margin: 0, font: '600 16px/1.2 var(--font-sans)' }}>
        {children}
      </h2>
    );
  }
  return (
    <h1 id={id} style={{ margin: 0, font: '600 24px/1.25 var(--font-display)', letterSpacing: '-0.01em' }}>
      {children}
    </h1>
  );
}
